// Per-page SEO for the public marketing surface.
//
// The app is a client-rendered SPA with ONE static <title> in index.html, so
// before this every page — blog posts included — reported the same title and
// description to crawlers and to anyone sharing a link. This hook sets the
// per-page title, meta description, canonical URL, Open Graph / Twitter card
// tags, and (on articles) JSON-LD Article structured data.
//
// Google renders JS before indexing, so setting these on mount is effective.
// Everything is cleaned up between route changes so tags never leak from one
// page to the next.

const SITE = "https://alieffundinghub.com";
const SITE_NAME = "Alief Funding Hub";

export interface SeoInput {
  title: string;
  description: string;
  /** Path only, e.g. "/blog/the-order-you-apply-in" */
  path: string;
  /** Set for blog posts — emits Article structured data. */
  article?: { published: string; modified?: string; readingMinutes?: number };
}

function upsertMeta(selector: string, attrs: Record<string, string>): HTMLElement {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  el.setAttribute("data-seo", "1");
  return el;
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][data-seo]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Call once per public page, at render time. */
export function applySeo({ title, description, path, article }: SeoInput) {
  if (typeof document === "undefined") return;
  const url = SITE + path;

  document.title = title;
  upsertMeta('meta[name="description"]', { name: "description", content: description });
  upsertLink("canonical", url);

  // Open Graph (link previews on iMessage, LinkedIn, Facebook…)
  upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
  upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
  upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
  upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
  upsertMeta('meta[property="og:type"]', { property: "og:type", content: article ? "article" : "website" });
  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });

  // Structured data — Article on posts, Organization elsewhere.
  const existing = document.getElementById("nx-jsonld");
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "nx-jsonld";
  script.textContent = JSON.stringify(
    article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          datePublished: article.published,
          dateModified: article.modified ?? article.published,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          author: { "@type": "Organization", name: "Alief Financial Services" },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE,
          },
          ...(article.readingMinutes ? { timeRequired: `PT${article.readingMinutes}M` } : {}),
        }
      : {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          legalName: "Alief Financial Services",
          url: SITE,
          description,
          contactPoint: {
            "@type": "ContactPoint",
            email: "support@alieffundinghub.com",
            contactType: "customer support",
            url: SITE + "/contact",
          },
        },
  );
  document.head.appendChild(script);
}
